/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import {
  Option,
  Select,
  TextInput,
  Button,
  Icons,
  Positions,
} from '@sede-enterprise/shell-ds-react-framework';
import { FilterContainer, FilterPopover } from '../../style';

import { filterOptionsNumber, filterOptionsString } from './filterOptions';
import {
  IFilterOptions,
  IFilter,
} from '../../../../types/UI/GenesisTable.type';

const { Filter, FilterSquare } = Icons;

const FilterOptions: React.FC<IFilterOptions> = ({
  column,
  handleFilter = () => {},
  filter: columnFilter,
}) => {
  const defaultValues: IFilter = {
    accessKey: '',
    field: '',
    type: 'text',
    filterOne: 'IsEqualTo',
    filterOneValue: '',
    condition: 'And',
    filterTwo: 'IsEqualTo',
    filterTwoValue: '',
    nestedProperty: undefined,
    nestedObject: undefined,
  };

  const [visibleFilter, setVisibleFilter] = useState<boolean>(false);
  const [appliedFilter, setAppliedFilter] = useState<boolean>(false);
  const [inputTypeOne, setInputTypeOne] = useState<string>(
    column?.isDigitGrouping ? 'text' : column?.type
  );
  const [inputTypeTwo, setInputTypeTwo] = useState<string>(
    column?.isDigitGrouping ? 'text' : column?.type
  );

  const methods = useForm();
  const { reset, control, resetField } = methods;

  const handleReset = () => {
    const resetValue = {
      ...defaultValues,
      field: column.fieldName,
      accessKey: column.accessKey,
      type: column?.type || 'text',
      nestedProperty: column?.nestedProperty || undefined,
      nestedObject: column?.nestedObject || undefined,
    };
    reset(resetValue);
    handleFilter(resetValue);
    setAppliedFilter(false);
  };

  useEffect(() => {
    if (columnFilter) {
      if (column?.isDigitGrouping) {
        reset({
          ...columnFilter,
          filterOneValue: columnFilter.filterOneValue
            ? parseFloat(columnFilter.filterOneValue)?.toLocaleString()
            : columnFilter.filterOneValue,
          filterTwoValue: columnFilter.filterTwoValue
            ? parseFloat(columnFilter.filterTwoValue)?.toLocaleString()
            : columnFilter.filterTwoValue,
        });
      } else {
        reset(columnFilter);
      }
      setAppliedFilter(true);
    } else {
      handleReset();
    }
  }, [columnFilter]);

  const handleSubmitForm = (data) => {
    let objFilter = {
      ...data,
    };

    if (data.filterOneValue === '' && data.filterTwoValue !== '') {
      objFilter = {
        ...objFilter,
        filterOne: data.filterTwo,
        filterTwo: 'IsEqualTo',
        filterOneValue: data.filterTwoValue,
        filterTwoValue: '',
      };
      resetField('filterOne', {
        defaultValue: data.filterTwo,
      });
      resetField('filterOneValue', {
        defaultValue: data.filterTwoValue,
      });
      resetField('filterTwo', {
        defaultValue: 'IsEqualTo',
      });
      resetField('', {
        defaultValue: 'filterTwoValue',
      });
    }

    if (column?.isDigitGrouping) {
      objFilter = {
        ...objFilter,
        filterOneValue: objFilter.filterOneValue.replaceAll(',', ''),
        filterTwoValue: objFilter.filterTwoValue.replaceAll(',', ''),
      };
    }
    if (data.filterOneValue || data.filterTwoValue) {
      setAppliedFilter(true);
    } else {
      setAppliedFilter(false);
    }
    if (!data.filterOneValue && !data.filterTwoValue) {
      return;
    }
    setVisibleFilter(false);
    handleFilter(objFilter);
  };

  const renderFilterOption = () => {
    const filterOption =
      column?.type === 'number' ||
      column?.type === 'datetime-local' ||
      column?.type === 'date'
        ? filterOptionsNumber
        : filterOptionsString;
    return (
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmitForm)}>
          <FilterContainer
            className={column?.type === 'datetime-local' ? 'date-time' : ''}
          >
            <span>Show items with value that:</span>
            <Controller
              control={control}
              name='filterOne'
              render={({ field: { value, onChange } }) => (
                <Select
                  showSearch={false}
                  onChange={onChange}
                  value={value}
                  data-testid={`filter-one-${column.fieldName}`}
                  allowClear={false}
                  size='small'
                  className='space'
                >
                  {filterOption.map((item) => (
                    <Option
                      key={`assignment_Opt_${item.value}`}
                      value={item.value}
                    >
                      <span> {item.title}</span>
                    </Option>
                  ))}
                </Select>
              )}
            />

            <Controller
              control={control}
              name='filterOneValue'
              render={({ field: { value, onChange } }) => (
                <TextInput
                  type={inputTypeOne}
                  size='small'
                  value={value}
                  className='space'
                  data-testid={`filter-one-value-${column.fieldName}`}
                  onChange={onChange}
                  onBlur={(event) => {
                    if (column?.isDigitGrouping) {
                      setInputTypeOne('text');
                      onChange(
                        event.target.value
                          ? parseFloat(event.target.value).toLocaleString()
                          : event.target.value
                      );
                    }
                  }}
                  onFocus={(event) => {
                    if (column?.isDigitGrouping) {
                      setInputTypeOne('number');
                      onChange(event.target.value.replaceAll(',', ''));
                    }
                  }}
                />
              )}
            />

            <Controller
              control={control}
              name='condition'
              render={({ field: { value, onChange } }) => (
                <Select
                  showSearch={false}
                  onChange={onChange}
                  value={value}
                  data-testid={`condition-${column.fieldName}`}
                  allowClear={false}
                  size='small'
                  className='space'
                >
                  <Option key='And' value='And'>
                    <span>And</span>
                  </Option>
                  <Option key='Or' value='Or'>
                    <span>Or</span>
                  </Option>
                </Select>
              )}
            />

            <Controller
              control={control}
              name='filterTwo'
              render={({ field: { value, onChange } }) => (
                <Select
                  showSearch={false}
                  onChange={onChange}
                  value={value}
                  data-testid={`filter-two-${column.fieldName}`}
                  allowClear={false}
                  size='small'
                  className='space'
                >
                  {filterOption.map((item) => (
                    <Option
                      key={`assignment_Opt_${item.value}`}
                      value={item.value}
                    >
                      <span> {item.title}</span>
                    </Option>
                  ))}
                </Select>
              )}
            />

            <Controller
              control={control}
              name='filterTwoValue'
              render={({ field: { value, onChange } }) => (
                <TextInput
                  type={inputTypeTwo}
                  size='small'
                  value={value}
                  className='space'
                  data-testid={`filter-two-value-${column.fieldName}`}
                  onChange={onChange}
                  onBlur={(event) => {
                    if (column?.isDigitGrouping) {
                      setInputTypeTwo('text');
                      onChange(
                        event.target.value
                          ? parseFloat(event.target.value).toLocaleString()
                          : event.target.value
                      );
                    }
                  }}
                  onFocus={(event) => {
                    if (column?.isDigitGrouping) {
                      setInputTypeTwo('number');
                      onChange(event.target.value.replaceAll(',', ''));
                    }
                  }}
                />
              )}
            />
            <div className='button-container'>
              <Button
                type='submit'
                size='small'
                // onClick={handleSubmitFilter}
                data-testid={`submit-filter-${column.fieldName}`}
              >
                Filter
              </Button>
              <Button
                type='button'
                variant='outlined'
                size='small'
                onClick={handleReset}
                data-testid={`clear-filter-${column.fieldName}`}
              >
                Clear
              </Button>
            </div>
          </FilterContainer>
        </form>
      </FormProvider>
    );
  };

  const renderButton = () => (
    <Button
      size='small'
      variant='transparent'
      type='button'
      data-testid={`filter-${column.fieldName}`}
      onClick={() => {
        setVisibleFilter(true);
      }}
      iconOnly
      icon={
        appliedFilter ? (
          <FilterSquare height={23} width={23} />
        ) : (
          <Filter height={15} width={15} />
        )
      }
    />
  );

  return (
    <FilterPopover
      action='click'
      arrow
      popupPlacement={Positions.BottomRight}
      popup={renderFilterOption()}
      popupVisible={visibleFilter}
      onPopupVisibleChange={(isOpen: boolean) => {
        setVisibleFilter(isOpen);
      }}
    >
      {renderButton()}
    </FilterPopover>
  );
};

export default FilterOptions;
